import React from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import {
  ShieldCheck,
  Certificate,
  UserFocus,
  Buildings,
  Globe,
  ClipboardText,
  CalendarBlank,
  Bank,
  ArrowRight,
  Star,
  CheckCircle,
  LockKey,
  Clock,
} from "@phosphor-icons/react";

const services = [
  { slug: "individual", name: "Individual Tax Filing", icon: UserFocus, desc: "1040, Schedules A–E, multi-state and RSU-heavy returns." },
  { slug: "business", name: "Business Tax Filing", icon: Buildings, desc: "1120, 1120-S, 1065 with QBI optimization and depreciation strategy." },
  { slug: "itin", name: "ITIN Application", icon: Certificate, desc: "W-7 preparation with Certifying Acceptance Agent guidance." },
  { slug: "fbar-fatca", name: "FBAR & FATCA", icon: Globe, desc: "FinCEN 114 and Form 8938 for US persons with foreign accounts." },
  { slug: "irs-notice", name: "IRS Notice Assistance", icon: ClipboardText, desc: "CP2000, CP14, LT11 and audit correspondence resolution." },
  { slug: "extensions", name: "Tax Extensions", icon: CalendarBlank, desc: "Form 4868 and 7004 filed the same day with payment vouchers." },
  { slug: "corporate", name: "Corporate Programs", icon: Bank, desc: "Managed tax services for CPA firms, EAs and enterprise finance." },
];

const STAR_KEYS = ["s1", "s2", "s3", "s4", "s5"];

export default function Home() {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden border-b tf-hairline">
        <div className="absolute inset-0 tf-grid opacity-60" aria-hidden />
        <div className="absolute inset-0 tf-noise" aria-hidden />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 rounded-full border tf-hairline bg-white px-3 py-1 text-xs font-mono uppercase tracking-widest text-[#0A2540]" data-testid="hero-badge">
              <span className="h-1.5 w-1.5 rounded-full bg-[#D32F2F] animate-pulse" />
              Now onboarding for the 2026 filing season
            </div>
            <h1 className="mt-6 font-[Chivo] text-5xl sm:text-6xl lg:text-7xl font-black leading-[0.95] tracking-tight text-[#0A2540]" data-testid="hero-title">
              Tax operations,<br />
              <span className="tf-brand-gradient-text">re-engineered</span> for<br />
              modern finance teams.
            </h1>
            <p className="mt-6 max-w-xl text-lg text-slate-600 leading-relaxed" data-testid="hero-subtitle">
              Taxfree Filer LLP is the U.S. tax filing specialist for NRIs, individuals and enterprise firms. Audit-defensible filings, IRS notice resolution and a secure client portal — trusted across the United States and 40+ countries.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register">
                <Button size="lg" className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white h-12 px-6" data-testid="hero-cta-primary">
                  Start Your Filing <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
              <Link to="/pricing">
                <Button size="lg" variant="outline" className="h-12 px-6 border-[#0A2540] text-[#0A2540]" data-testid="hero-cta-secondary">
                  See Pricing
                </Button>
              </Link>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-6 max-w-lg">
              {[
                ["12,400+", "Returns filed"],
                ["$18.7M", "Refunds delivered"],
                ["4.9/5", "Client rating"],
              ].map(([v, l]) => (
                <div key={l}>
                  <div className="font-[Chivo] text-2xl font-black text-[#0A2540]">{v}</div>
                  <div className="text-xs text-slate-500 uppercase tracking-widest mt-1">{l}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-4 bg-[#D32F2F]/10 rounded-lg -rotate-2" aria-hidden />
              <div className="relative rounded-lg overflow-hidden border tf-hairline bg-white">
                <img
                  src="https://images.unsplash.com/photo-1775163024488-e88e4a71179f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwyfHxhY2NvdW50aW5nJTIwcHJvZmVzc2lvbmFsJTIwb2ZmaWNlfGVufDB8fHx8MTc4NDExMDgwMXww&ixlib=rb-4.1.0&q=85"
                  alt="Tax professionals reviewing documents"
                  className="w-full h-[400px] object-cover"
                />
                <div className="p-5 border-t tf-hairline">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-[#0A2540] text-white grid place-items-center font-[Chivo] font-black">JR</div>
                    <div>
                      <div className="font-semibold text-[#0A2540] text-sm">Jordan Rivers, CPA</div>
                      <div className="text-xs text-slate-500">Senior Tax Partner · 14 yrs experience</div>
                    </div>
                  </div>
                  <p className="mt-3 text-sm text-slate-600">
                    “We built Taxfree Filer to replace spreadsheets and PDF chains with a real operations platform.”
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="border-b tf-hairline bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 py-6 flex flex-wrap items-center justify-between gap-6">
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500">
            Trusted by tax teams at
          </div>
          <div className="flex flex-wrap items-center gap-x-10 gap-y-3 text-sm font-[Chivo] font-bold text-slate-400">
            <span>NORTHRIDGE CPA</span>
            <span>MERIDIAN TAX GROUP</span>
            <span>ATLAS ENROLLED AGENTS</span>
            <span>OAKLINE PARTNERS</span>
            <span>SIGNAL FINANCE LLP</span>
          </div>
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section className="mx-auto max-w-7xl px-6 lg:px-8 py-20 lg:py-24" id="services">
        <div className="flex items-end justify-between flex-wrap gap-6">
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-[#D32F2F]">01 · Services</div>
            <h2 className="mt-3 font-[Chivo] text-4xl lg:text-5xl font-black text-[#0A2540] tracking-tight">A full-stack tax practice, on demand.</h2>
          </div>
          <p className="max-w-md text-slate-600">From simple 1040s to consolidated 1120 filings and international disclosures — one operations layer, one accountable team.</p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border tf-hairline">
          {services.map(({ slug, name, icon: Icon, desc }) => (
            <Link
              to={`/services/${slug}`}
              key={slug}
              data-testid={`service-card-${slug}`}
              className="group bg-white p-8 hover:bg-[#0A2540] hover:text-white transition-colors relative"
            >
              <Icon size={32} weight="duotone" className="text-[#D32F2F] group-hover:text-white" />
              <h3 className="mt-6 font-[Chivo] text-xl font-bold">{name}</h3>
              <p className="mt-3 text-sm text-slate-600 group-hover:text-slate-200 leading-relaxed">{desc}</p>
              <div className="mt-6 inline-flex items-center gap-1 text-sm font-semibold">
                Learn more <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* MISSION CONTROL PREVIEW */}
      <section className="bg-[#0A2540] text-white relative overflow-hidden">
        <div className="absolute inset-0 tf-grid opacity-10" aria-hidden />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-8 py-20 lg:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-[#D32F2F]">02 · Mission Control</div>
            <h2 className="mt-3 font-[Chivo] text-4xl lg:text-5xl font-black tracking-tight">One dashboard. Every filing, notice and payment.</h2>
            <p className="mt-5 text-slate-300 max-w-lg leading-relaxed">
              Track returns by stage, resolve IRS notices, collect signatures and view real-time analytics — with role-based access for partners, staff and clients.
            </p>
            <ul className="mt-8 space-y-3">
              {[
                "Kanban workflow across Intake → Prep → Review → Filed",
                "IRS notice register with due-date SLAs",
                "Secure client portal with signed document exchange",
                "Role-based access: Admin, CPA, Client",
              ].map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm text-slate-200">
                  <CheckCircle size={20} weight="fill" className="text-[#D32F2F] shrink-0 mt-0.5" />
                  {f}
                </li>
              ))}
            </ul>
            <div className="mt-8 flex gap-3">
              <Link to="/register"><Button className="bg-[#D32F2F] hover:bg-[#D32F2F]/90 h-11" data-testid="mc-cta-register">Open a Client Account</Button></Link>
              <Link to="/login"><Button variant="outline" className="h-11 border-white/30 text-white hover:bg-white hover:text-[#0A2540]" data-testid="mc-cta-login">Client Login</Button></Link>
            </div>
          </div>
          <div className="rounded-lg overflow-hidden border border-white/10 bg-white/5 backdrop-blur">
            <img
              src="https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxmaW5hbmNpYWwlMjBkYXRhJTIwYWJzdHJhY3QlMjBibHVlfGVufDB8fHx8MTc4NDExMDgwMnww&ixlib=rb-4.1.0&q=85"
              alt="Financial analytics dashboard"
              className="w-full h-[440px] object-cover"
            />
          </div>
        </div>
      </section>

      {/* SECURITY */}
      <section className="mx-auto max-w-7xl px-6 lg:px-8 py-20 lg:py-24 grid lg:grid-cols-5 gap-12 items-center">
        <div className="lg:col-span-2 rounded-lg overflow-hidden border tf-hairline">
          <img
            src="https://images.unsplash.com/photo-1695668548342-c0c1ad479aee?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwyfHxzZWN1cmUlMjBzZXJ2ZXIlMjB0ZWNobm9sb2d5fGVufDB8fHx8MTc4NDExMDgwMnww&ixlib=rb-4.1.0&q=85"
            alt="Secure infrastructure"
            className="w-full h-[380px] object-cover"
          />
        </div>
        <div className="lg:col-span-3">
          <div className="text-xs font-mono uppercase tracking-widest text-[#D32F2F]">03 · Security & Trust</div>
          <h2 className="mt-3 font-[Chivo] text-4xl lg:text-5xl font-black text-[#0A2540] tracking-tight">Bank-grade custody for tax records.</h2>
          <p className="mt-5 text-slate-600 leading-relaxed max-w-xl">
            We treat your W-2s, K-1s and 1099s like financial assets — with encryption in transit and at rest, audit logs, and multi-factor authentication across the platform.
          </p>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              { icon: LockKey, title: "256-bit TLS", desc: "End-to-end encryption on every request." },
              { icon: ShieldCheck, title: "SOC 2 aligned", desc: "Controls modeled on SOC 2 Type II." },
              { icon: Clock, title: "7-year retention", desc: "Aligned with IRS record retention rules." },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="border-l-2 border-[#D32F2F] pl-4">
                <Icon size={22} weight="duotone" className="text-[#0A2540]" />
                <div className="mt-2 font-[Chivo] font-bold text-[#0A2540]">{title}</div>
                <div className="text-sm text-slate-600 mt-1">{desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="border-y tf-hairline bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 py-20">
          <div className="text-xs font-mono uppercase tracking-widest text-[#D32F2F]">04 · Voices</div>
          <h2 className="mt-3 font-[Chivo] text-4xl lg:text-5xl font-black text-[#0A2540] tracking-tight max-w-2xl">Firms we serve don't switch back.</h2>
          <div className="mt-14 grid md:grid-cols-3 gap-8">
            {[
              { q: "Our client portal complaints dropped to zero. Every partner reviews returns in the same workflow — it just works.", a: "Michael O'Neil", r: "Enrolled Agent · Atlas EA" },
              { q: "As a solo CPA I finally look like a firm. Signatures, document collection and analytics without a stack of vendors.", a: "Dana Whitfield, CPA", r: "Whitfield Advisory" },
              { q: "Migration from our legacy stack took a weekend. The audit trail alone is worth the switch for any multi-partner firm.", a: "Priya Rajan, CPA", r: "Managing Partner · Beacon Tax" },
            ].map((t) => (
              <figure key={t.a} className="rounded-md border tf-hairline bg-slate-50 p-6">
                <div className="flex gap-1 text-[#D32F2F]">
                  {STAR_KEYS.map((k) => (<Star key={k} size={14} weight="fill" />))}
                </div>
                <blockquote className="mt-4 text-slate-800 leading-relaxed">“{t.q}”</blockquote>
                <figcaption className="mt-6 pt-4 border-t tf-hairline text-sm">
                  <div className="font-semibold text-[#0A2540]">{t.a}</div>
                  <div className="text-slate-500 text-xs mt-0.5">{t.r}</div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-6 lg:px-8 py-20">
        <div className="rounded-lg border tf-hairline bg-white p-10 lg:p-16 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="font-[Chivo] text-4xl lg:text-5xl font-black text-[#0A2540] tracking-tight">Ready to file smarter this season?</h2>
            <p className="mt-4 text-slate-600 max-w-lg">Open a free client account, upload your documents, and get matched with a licensed preparer in under 48 hours.</p>
          </div>
          <div className="flex flex-wrap gap-3 lg:justify-end">
            <Link to="/register"><Button size="lg" className="bg-[#D32F2F] hover:bg-[#D32F2F]/90 h-12 px-6" data-testid="footer-cta-register">Create Account</Button></Link>
            <Link to="/contact"><Button size="lg" variant="outline" className="h-12 px-6 border-[#0A2540] text-[#0A2540]" data-testid="footer-cta-contact">Talk to an advisor</Button></Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
